import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";
import { getPageSeo, breadcrumbSchema } from "../lib/seo";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#101F27] text-white min-h-screen">
      <SEO
        {...getPageSeo("/privacy-policy")}
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Privacy Policy", path: "/privacy-policy" },
        ])}
      />
      {/* Page Header */}
      <section className="bg-[#37464F]/30 border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <Link
            to="/"
            className="inline-flex items-center text-[#009688] hover:underline mb-6 text-sm font-semibold"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h2 className="text-base md:text-lg font-semibold text-[#009688] uppercase tracking-wide mb-4">
            Legal
          </h2>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Privacy Policy
          </h1>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-invert max-w-none">
            <p className="text-lg text-gray-300 leading-relaxed mb-6 font-semibold">
              WristCoin Privacy Policy
            </p>

            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              This Privacy Policy describes how your personal information is collected, used, and shared when you visit or make a purchase from mywristcoin.com (the "Site").
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">PERSONAL INFORMATION WE COLLECT</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              When you visit the Site, we automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the cookies that are installed on your device. Additionally, as you browse the Site, we collect information about the individual web pages or products that you view, what websites or search terms referred you to the Site, and information about how you interact with the Site. We refer to this automatically-collected information as "Device Information."
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-4">
              We collect Device Information using the following technologies:
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-4">
              – "Cookies" are data files that are placed on your device or computer and often include an anonymous unique identifier. For more information about cookies, and how to disable cookies, visit{" "}
              <a href="http://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-[#009688] hover:underline">http://www.allaboutcookies.org</a>.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-4">
              – "Log files" track actions occurring on the Site, and collect data including your IP address, browser type, Internet service provider, referring/exit pages, and date/time stamps.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              – "Web beacons," "tags," and "pixels" are electronic files used to record information about how you browse the Site.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Additionally when you make a purchase or attempt to make a purchase through the Site, we collect certain information from you, including your name, billing address, shipping address, payment information (including credit card numbers [[INSERT ANY OTHER PAYMENT TYPES ACCEPTED]]), email address, and phone number. We refer to this information as "Order Information."
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              When we talk about "Personal Information" in this Privacy Policy, we are talking both about Device Information and Order Information.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">HOW DO WE USE YOUR PERSONAL INFORMATION?</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-4">
              We use the Order Information that we collect generally to fulfill any orders placed through the Site (including processing your payment information, arranging for shipping, and providing you with invoices and/or order confirmations). Additionally, we use this Order Information to:
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-2">
              Communicate with you;
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-2">
              Screen our orders for potential risk or fraud; and
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              When in line with the preferences you have shared with us, provide you with information or advertising relating to our products or services.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              We use the Device Information that we collect to help us screen for potential risk and fraud (in particular, your IP address), and more generally to improve and optimize our Site (for example, by generating analytics about how our customers browse and interact with the Site, and to assess the success of our marketing and advertising campaigns).
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">SHARING YOUR PERSONAL INFORMATION</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              We share your Personal Information with third parties to help us use your Personal Information, as described above. For example, we use Shopify to power our online store–you can read more about how Shopify uses your Personal Information here:{" "}
              <a href="https://www.shopify.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-[#009688] hover:underline">https://www.shopify.com/legal/privacy</a>. We also use Google Analytics to help us understand how our customers use the Site–you can read more about how Google uses your Personal Information here:{" "}
              <a href="https://www.google.com/intl/en/policies/privacy/" target="_blank" rel="noopener noreferrer" className="text-[#009688] hover:underline">https://www.google.com/intl/en/policies/privacy/</a>. You can also opt-out of Google Analytics here:{" "}
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[#009688] hover:underline">https://tools.google.com/dlpage/gaoptout</a>.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              Finally, we may also share your Personal Information to comply with applicable laws and regulations, to respond to a subpoena, search warrant or other lawful request for information we receive, or to otherwise protect our rights.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">BEHAVIOURAL ADVERTISING</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              As described above, we use your Personal Information to provide you with targeted advertisements or marketing communications we believe may be of interest to you. For more information about how targeted advertising works, you can visit the Network Advertising Initiative's ("NAI") educational page at{" "}
              <a href="http://www.networkadvertising.org/understanding-online-advertising/how-does-it-work" target="_blank" rel="noopener noreferrer" className="text-[#009688] hover:underline">http://www.networkadvertising.org/understanding-online-advertising/how-does-it-work</a>.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              Additionally, you can opt out of some of these services by visiting the Digital Advertising Alliance's opt-out portal at:{" "}
              <a href="http://optout.aboutads.info/" target="_blank" rel="noopener noreferrer" className="text-[#009688] hover:underline">http://optout.aboutads.info/</a>.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">DO NOT TRACK</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              Please note that we do not alter our Site's data collection and use practices when we see a Do Not Track signal from your browser.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">YOUR RIGHTS</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              If you are a European resident, you have the right to access personal information we hold about you and to ask that your personal information be corrected, updated, or deleted. If you would like to exercise this right, please contact us through the contact information below.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              Additionally, if you are a European resident we note that we are processing your information in order to fulfill contracts we might have with you (for example if you make an order through the Site), or otherwise to pursue our legitimate business interests listed above. Additionally, please note that your information will be transferred outside of Europe, including to Canada and the United States.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">DATA RETENTION</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              When you place an order through the Site, we will maintain your Order Information for our records unless and until you ask us to delete this information.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">CHANGES</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              We may update this privacy policy from time to time in order to reflect, for example, changes to our practices or for other operational, legal or regulatory reasons.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">CONTACT US</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              For more information about our privacy practices, if you have questions, or if you would like to make a complaint, please contact us by e-mail at{" "}
              <a href="mailto:info@mywristcoin.com" className="text-[#009688] hover:underline">info@mywristcoin.com</a>{" "}
              or by mail using the details provided below:
            </p>
          </article>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
